"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import Image from "next/image"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import TrackableLink from "@/components/TrackableLink"

const navigation = [
  { name: "Inventory", href: "/for-sale-vehicles" },
  { name: "Ready to Go", href: "/ready-to-go" },
  { name: "Marked Down", href: "/price-marked-down" },
  { name: "Coming Soon", href: "/coming-soon-newly-added" },
  { name: "Contact", href: "/contact-information" },
]

const mobileNavigation = [
  { name: "Inventory", href: "/for-sale-vehicles" },
  { name: "Ready to Go", href: "/ready-to-go" },
  { name: "Marked Down", href: "/price-marked-down" },
  { name: "Coming Soon", href: "/coming-soon-newly-added" },
  { name: "Contact", href: "/contact-information" },
]

const moreLinks = [
  { name: "Gallery", href: "/gallery" },
  { name: "USA Shipping", href: "/shipping-rates/usa-domestic" },
  { name: "International Shipping", href: "/shipping-rates/international" },
  { name: "FAQ", href: "/frequently-asked-questions" },
  { name: "Financing", href: "/financing-information" },
  { name: "All Makes & Models", href: "/all-makes-and-models" },
]

export default function Header() {
  const router = useRouter()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/for-sale-vehicles?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  function closeMenu() {
    setMobileMenuOpen(false)
  }

  return (
    <header className="sticky lg:static top-0 z-50 bg-black shadow-sm">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8">
        <Link href="/" className="flex items-center gap-3 shrink-0">
          <Image
            src="/img/logo/autols-modern.png"
            alt="AutoLS"
            width={120}
            height={40}
            className="h-10 w-auto"
            priority
          />
          <span
            className="hidden sm:inline font-bold text-xl sm:text-2xl"
            style={{
              fontFamily: "Montserrat, sans-serif",
              letterSpacing: "0.12em",
              background: "linear-gradient(to right, #E6B65C, #B8892E)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            AUTO LIFESTYLE
          </span>
        </Link>

        <form onSubmit={handleSearch} className="flex-1 max-w-xs mx-3 sm:hidden">
          <div className="flex">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search vehicles..."
              className="block w-full rounded-l-md border border-r-0 border-gray-600 bg-gray-900 px-3 py-2 text-sm text-white placeholder-gray-400 focus:border-amber-500 focus:outline-none"
            />
            <button
              type="submit"
              className="flex shrink-0 items-center rounded-r-md bg-amber-500 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-5">
                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </form>

        <div className="hidden lg:flex lg:items-center lg:divide-x lg:divide-gray-700">
          {navigation.map((item) => (
            <Link
              key={item.name}
              href={item.href}
              className="px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 hover:text-amber-400 border border-transparent hover:border-gray-600 rounded transition-all"
            >
              {item.name}
            </Link>
          ))}
          <Menu as="div" className="relative">
            <MenuButton className="group flex items-center gap-1 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 hover:text-amber-400 border border-transparent hover:border-gray-600 rounded transition-all focus:outline-none">
              More
              <svg
                className="h-4 w-4 transition-transform group-data-open:rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </MenuButton>
            <MenuItems
              anchor={{ to: "bottom end", gap: 4 }}
              className="w-48 rounded-lg bg-gray-900 border border-gray-700 shadow-lg py-1 z-50 focus:outline-none"
            >
              {moreLinks.map((item) => (
                <MenuItem key={item.name}>
                  <Link
                    href={item.href}
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-amber-400 data-focus:bg-gray-800 data-focus:text-amber-400"
                  >
                    {item.name}
                  </Link>
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
        </div>

        <div className="flex items-center gap-x-4">
          <TrackableLink
            href="tel:8015081711"
            type="phone"
            className="hidden sm:flex items-center gap-2 text-xs text-zinc-400 hover:text-amber-400 transition-colors"
          >
            <i className="bootstrap-telephone-fill text-lg text-amber-400" />
            (801) 508-1711
          </TrackableLink>

          <button
            type="button"
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-600 text-white"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm"
            onClick={closeMenu}
          />

          <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-black shadow-xl">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                <Link href="/" onClick={closeMenu}>
                  <Image
                    src="/img/logo/autols-modern.png"
                    alt="AutoLS"
                    width={100}
                    height={33}
                    className="h-8 w-auto"
                  />
                </Link>
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white hover:text-amber-400"
                  onClick={closeMenu}
                  aria-label="Close menu"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <nav className="flex-1 px-6 py-6 overflow-y-auto">
                <ul className="space-y-1">
                  {mobileNavigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        className="block rounded-lg px-4 py-3 text-lg font-medium text-white hover:bg-gray-800"
                        onClick={closeMenu}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      onClick={() => setMoreOpen(!moreOpen)}
                      className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-lg font-medium text-white hover:bg-gray-800"
                    >
                      More
                      <svg
                        className={`h-5 w-5 transition-transform ${moreOpen ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                    {moreOpen && (
                      <ul className="mt-1 ml-4 space-y-1 border-l border-gray-700 pl-4">
                        {moreLinks.map((item) => (
                          <li key={item.name}>
                            <Link
                              href={item.href}
                              className="block rounded-lg px-4 py-2 text-base text-zinc-400 hover:text-white hover:bg-gray-800"
                              onClick={closeMenu}
                            >
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                </ul>

                <TrackableLink
                  href="tel:8015081711"
                  type="phone"
                  className="mt-6 flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-3 text-base font-semibold text-white hover:bg-amber-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                  (801) 508-1711
                </TrackableLink>
              </nav>

            </div>
          </div>
        </div>
      )}
    </header>
  )
}
